export interface SkillItem {
  name: string;
  category: string;
}

export interface ProjectItem {
  title: string;
  description: string;
  tags: string[];
}

export interface Section {
  id: string;
  title: string;
  icon: string;
  color: string;
  position: [number, number, number];
  content: SectionContent;
}

export interface SectionContent {
  heading: string;
  description?: string;
  skills?: SkillItem[];
  projects?: ProjectItem[];
  experience?: ExperienceItem[];
  contact?: ContactInfo;
}

export interface ExperienceItem {
  role: string;
  description: string;
}

export interface ContactInfo {
  email: string;
  github: string;
  linkedin: string;
}

const PHI = (1 + Math.sqrt(5)) / 2;

function fibonacciSphere(index: number, total: number, radius: number): [number, number, number] {
  const y = 1 - (index / (total - 1)) * 2;
  const radiusAtY = Math.sqrt(1 - y * y);
  const theta = 2 * Math.PI * index / PHI;
  return [
    Math.cos(theta) * radiusAtY * radius,
    y * radius,
    Math.sin(theta) * radiusAtY * radius,
  ];
}

export const sections: Section[] = [
  {
    id: 'about',
    title: 'About Me',
    icon: '👤',
    color: '#00f0ff',
    position: fibonacciSphere(0, 9, 2.2),
    content: {
      heading: 'About Me',
      description:
        'I am a software developer transitioning into DevOps, Cloud, and Platform Engineering. I have experience in SAP ABAP development, production support, Linux, automation, CI/CD, Kubernetes, Docker, Terraform, Prometheus, Grafana, GitHub Actions, Azure DevOps, and cloud infrastructure labs.\n\nI am passionate about building reliable infrastructure, automating everything, and creating seamless developer experiences. My journey bridges enterprise SAP systems with modern cloud-native practices.',
    },
  },
  {
    id: 'skills',
    title: 'Skills',
    icon: '⚡',
    color: '#4060ff',
    position: fibonacciSphere(1, 9, 2.2),
    content: {
      heading: 'Technical Skills',
      skills: [
        { name: 'Linux Administration', category: 'Infrastructure' },
        { name: 'Docker', category: 'Containers' },
        { name: 'Kubernetes', category: 'Orchestration' },
        { name: 'Minikube', category: 'Orchestration' },
        { name: 'Terraform', category: 'IaC' },
        { name: 'GitHub Actions', category: 'CI/CD' },
        { name: 'Azure DevOps', category: 'CI/CD' },
        { name: 'GitLab CI/CD', category: 'CI/CD' },
        { name: 'Jenkins', category: 'CI/CD' },
        { name: 'Prometheus', category: 'Monitoring' },
        { name: 'Grafana', category: 'Monitoring' },
        { name: 'Node Exporter', category: 'Monitoring' },
        { name: 'Bash Scripting', category: 'Automation' },
        { name: 'SAP ABAP', category: 'Enterprise' },
        { name: 'SAP FI-CA', category: 'Enterprise' },
        { name: 'SAP HCM', category: 'Enterprise' },
        { name: 'SAP IS-U', category: 'Enterprise' },
        { name: 'Production Support', category: 'Operations' },
        { name: 'Incident Troubleshooting', category: 'Operations' },
        { name: 'CI/CD Pipeline Automation', category: 'CI/CD' },
        { name: 'Cloud Infrastructure', category: 'Cloud' },
        { name: 'Platform Engineering', category: 'Cloud' },
      ],
    },
  },
  {
    id: 'devops-cloud',
    title: 'DevOps & Cloud',
    icon: '☁️',
    color: '#8040ff',
    position: fibonacciSphere(2, 9, 2.2),
    content: {
      heading: 'DevOps & Cloud Projects',
      projects: [
        {
          title: 'Kubernetes & Cloud HomeLab',
          description:
            'Built local Kubernetes environments using Minikube and Multipass VMs. Practiced Deployments, Services, ConfigMaps, Namespaces, health checks, port forwarding, monitoring, and service exposure.',
          tags: ['Kubernetes', 'Minikube', 'Multipass', 'Docker'],
        },
        {
          title: 'Backstage Developer Portal Lab',
          description:
            'Experimented with Backstage as an internal developer portal, including GitHub authentication, catalog setup, scaffolder configuration, TechDocs, and plugin troubleshooting.',
          tags: ['Backstage', 'Developer Portal', 'GitHub Auth', 'TechDocs'],
        },
      ],
    },
  },
  {
    id: 'kubernetes',
    title: 'K8s & Monitoring',
    icon: '🎯',
    color: '#ff40c0',
    position: fibonacciSphere(3, 9, 2.2),
    content: {
      heading: 'Kubernetes & Monitoring',
      projects: [
        {
          title: 'Prometheus & Grafana Monitoring Lab',
          description:
            'Created an observability lab using Prometheus, Node Exporter, and Grafana. Monitored Linux VMs, Docker containers, application metrics, and infrastructure health.',
          tags: ['Prometheus', 'Grafana', 'Node Exporter', 'Observability'],
        },
        {
          title: 'Kubernetes Cluster Management',
          description:
            'Deployed and managed Kubernetes clusters with health checks, resource limits, rolling updates, and namespace isolation for multi-environment workflows.',
          tags: ['Kubernetes', 'Deployments', 'Services', 'ConfigMaps'],
        },
      ],
    },
  },
  {
    id: 'cicd',
    title: 'CI/CD',
    icon: '🔄',
    color: '#00ff80',
    position: fibonacciSphere(4, 9, 2.2),
    content: {
      heading: 'CI/CD Projects',
      projects: [
        {
          title: 'CI/CD Automation Labs',
          description:
            'Created CI/CD workflows using GitHub Actions, Azure DevOps, GitLab CI/CD, and Jenkins. Built pipelines for build, test, Docker image creation, Docker Hub push, and remote deployment to Linux VMs.',
          tags: ['GitHub Actions', 'Azure DevOps', 'GitLab CI', 'Jenkins'],
        },
        {
          title: 'Jenkins Deployment Pipeline',
          description:
            'Built a Jenkins multibranch pipeline that checks out code from Git, builds a Docker image, logs into Docker Hub, pushes the image, and deploys the container to an application VM using SSH credentials.',
          tags: ['Jenkins', 'Docker', 'SSH', 'Multibranch'],
        },
      ],
    },
  },
  {
    id: 'sap',
    title: 'SAP Enterprise',
    icon: '🏢',
    color: '#ffaa00',
    position: fibonacciSphere(5, 9, 2.2),
    content: {
      heading: 'SAP & Enterprise Work',
      projects: [
        {
          title: 'SAP Production Support',
          description:
            'Supported enterprise SAP systems involving bank statements, payment processing, batch jobs, file handling, FEBAN, FPY1, SM37, AL11, SFTP, and production issue troubleshooting.',
          tags: ['SAP', 'FEBAN', 'FPY1', 'Production Support'],
        },
      ],
    },
  },
  {
    id: 'automation',
    title: 'Automation',
    icon: '🤖',
    color: '#ff6040',
    position: fibonacciSphere(6, 9, 2.2),
    content: {
      heading: 'Automation Work',
      projects: [
        {
          title: 'SAP ABAP Automation',
          description:
            'Worked on ABAP development and automation tasks including reports, SmartForms, BAPIs, data updates, file processing, and system enhancements.',
          tags: ['ABAP', 'SmartForms', 'BAPIs', 'Automation'],
        },
      ],
    },
  },
  {
    id: 'experience',
    title: 'Experience',
    icon: '💼',
    color: '#40c0ff',
    position: fibonacciSphere(7, 9, 2.2),
    content: {
      heading: 'Work Experience',
      experience: [
        {
          role: 'SAP ABAP Developer / Production Support',
          description:
            'Supported enterprise SAP systems, automated reporting and data processes using ABAP, managed batch jobs, resolved production incidents, and maintained system stability across SAP FI-CA, HCM, and IS-U modules.',
        },
        {
          role: 'DevOps Intern',
          description:
            'Built CI/CD pipelines, containerized applications with Docker, deployed to Kubernetes clusters, set up monitoring with Prometheus and Grafana, and automated infrastructure provisioning.',
        },
      ],
    },
  },
  {
    id: 'contact',
    title: 'Contact',
    icon: '📬',
    color: '#00f0ff',
    position: fibonacciSphere(8, 9, 2.2),
    content: {
      heading: 'Get In Touch',
      description: "I'm always open to new opportunities, collaborations, and conversations about DevOps, Cloud, and Platform Engineering. Let's connect!",
      contact: {
        email: 'lsktheledi@gmail.com',
        github: 'https://github.com/naughlan-creator/',
        linkedin: 'https://www.linkedin.com/in/lehlogonolo-theledi-66a7b2285/',
      },
    },
  },
];

export const categoryColors: Record<string, string> = {
  Infrastructure: '#00f0ff',
  Containers: '#4060ff',
  Orchestration: '#8040ff',
  IaC: '#ff40c0',
  'CI/CD': '#00ff80',
  Monitoring: '#ffaa00',
  Automation: '#ff6040',
  Enterprise: '#40c0ff',
  Operations: '#ff8060',
  Cloud: '#a080ff',
};
